const RETURN_PATH_PARAM = 'redirect'

/**
 * Validates a return path resolves to somewhere on this site.
 *
 * Resolving against the current origin and comparing the result is used rather
 * than checking the string's prefix, because browsers normalise backslashes to
 * slashes in http(s) URLs: "/\evil.com" looks relative but resolves off-site.
 * Letting the URL parser decide avoids having to enumerate such cases.
 */
export function isSafeReturnPath(path: string | null): boolean {
  if (!path) {
    return false
  }

  try {
    return new URL(path, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

/**
 * Reads the validated return path from the current URL, if there is one.
 */
export function getReturnPath(): string | null {
  const path = new URLSearchParams(window.location.search).get(RETURN_PATH_PARAM)

  return isSafeReturnPath(path) ? path : null
}

/**
 * Appends a return path to a URL, so the user can be sent back there afterwards.
 *
 * Defaults to the current path, which is what a page gating itself behind login
 * wants. Pass `path` to forward a return path this page was itself given, rather
 * than pointing back at this page.
 */
export function withReturnPath(url: string, path?: string): string {
  const { pathname, search } = window.location
  const returnTo = path ?? `${pathname}${search}`
  const separator = url.includes('?') ? '&' : '?'

  return `${url}${separator}${RETURN_PATH_PARAM}=${encodeURIComponent(returnTo)}`
}
